import React, { useContext } from 'react'
import styled from 'styled-components'
import { ResultSummary } from './resultSummary'
import i18n from 'live/services/i18n'
import ActionPlanForm from './actionPlanForm'
import { ResultContext } from './qualityAnalysis'
import { getEmptyActionPlan } from './analysisUtils'
import { motion, AnimatePresence } from 'framer-motion'

const containerVariants = {
    hidden: {
        transition: {}
    },
    visible: {
        transition: {}
    }
}

export const resultsVariants = {
    hidden: { opacity: 0, y: '100vh' },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8 }
    }
}

const boxVariants = {
    hidden: { opacity: 0, y: '100vh' },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay: 0.5 }
    }
}

const formVariants = {
    hidden: { opacity: 0, y: '100vh' },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay: 0.1 }
    }
}
interface IProps {
    renderConditional: boolean
}
export function AnalysisResult({ renderConditional }: IProps): JSX.Element {
    const { actionPlans, setActionPlans, finalScore, analysisType } = useContext(ResultContext)
    const showActionPlans = analysisType !== 'icm' && finalScore <= 50
    const isActionPlanMandatory = analysisType === 'icc'

    function handleAddNewPlan(event): void {
        event.preventDefault()
        setActionPlans([...actionPlans, getEmptyActionPlan()])
    }

    return (
        <AnimatePresence>
            {renderConditional ? (
                <StyledAnalysisResult
                    className={`analysisResult ${showActionPlans ? '' : 'onlyOneBox'}`}
                    variants={containerVariants}
                    initial={'hidden'}
                    animate={'visible'}
                    exit={'hidden'}
                >
                    <ResultSummary />
                    {showActionPlans ? (
                        actionPlans.length ? (
                            <motion.form className={'analysisResult--actionPlans'} variants={formVariants}>
                                {actionPlans.map((actionPlan, index) => {
                                    return <ActionPlanForm key={index} index={index} {...actionPlan} />
                                })}
                                <button
                                    className={'analysisResult--actionPlans__addActionBtn'}
                                    onClick={event => handleAddNewPlan(event)}
                                >
                                    <i className="far fa-plus" />
                                    {`${i18n('Add')} ${i18n('Action')}`}
                                </button>
                            </motion.form>
                        ) : (
                            <motion.div
                                className="analysisResult--createActionPlan"
                                variants={boxVariants}
                                initial={'hidden'}
                                animate={'visible'}
                                exit={'hidden'}
                            >
                                <i className="far fa-file-alt" />
                                <span>
                                    {isActionPlanMandatory
                                        ? i18n('Action Plan Mandatory')
                                        : i18n('Action Plan Not Mandatory')}
                                </span>
                                <button
                                    className="analysisResult--createActionPlan__button"
                                    onClick={() => setActionPlans([getEmptyActionPlan()])}
                                >
                                    {i18n('Create Action Plan')}
                                </button>
                            </motion.div>
                        )
                    ) : null}
                </StyledAnalysisResult>
            ) : null}
        </AnimatePresence>
    )
}

const StyledAnalysisResult = styled(motion.div)`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    column-gap: 20px;
    align-items: start;
    padding-top: 10px;
    width: 100%;
    &.onlyOneBox {
        display: grid;
        justify-content: center;
    }
    @media (max-width: 1100px) {
        display: flex;
        flex-direction: column;
        align-items: stretch;
    }
    .analysisResult {
        &--createActionPlan {
            background: ${({ theme }) => theme['gray-300']};
            border-radius: 3px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 16px;
            color: ${({ theme }) => theme['gray-800']};
            height: fit-content;
            font-size: 15px;
            margin-bottom: 100px;
            i {
                color: ${({ theme }) => theme['gray-500']};
                font-size: 24px;
                margin-bottom: 8px;
            }
            &__button {
                border: none;
                background: none;
                color: ${({ theme }) => theme['blue-500']};
            }
        }
        &--actionPlans {
            border: 1px solid var(--gray-300);
            border-radius: 3px;
            display: flex;
            flex-direction: column;
            margin-bottom: 100px;
            &__addActionBtn {
                border: none;
                background: var(--blue-200);
                color: var(--blue-500);
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 8px;
                font-size: 14px;
                i {
                    margin-right: 5px;
                }
            }
        }
    }
`
